import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchSSE } from '@/utils/fetchSSE';
import type { ScoringProgress } from '@/components/pickems/ScoringProgressModal';

interface UseScoringProgressOptions {
  pickemId: string | null;
  enabled: boolean;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

interface SSEMessage {
  type: 'connected' | 'progress';
  data?: ScoringProgress;
}

export function useScoringProgress({
  pickemId,
  enabled,
  onComplete,
  onError,
}: UseScoringProgressOptions) {
  const [progress, setProgress] = useState<ScoringProgress | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const MAX_RECONNECT_ATTEMPTS = 5;

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  const disconnect = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!pickemId || !enabled) return;

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const url = `${apiUrl}/api/admin/pickems/${pickemId}/scoring-stream`;

    const abort = new AbortController();
    abortRef.current = abort;

    const handleConnectionLost = (reason: string) => {
      if (abort.signal.aborted) return;
      setIsConnected(false);

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error('[SSE] Max reconnect attempts reached:', reason);
        if (onErrorRef.current) {
          onErrorRef.current('Connection lost. Please refresh the page.');
        }
      }
    };

    setIsConnected(true);
    reconnectAttemptsRef.current = 0;

    fetchSSE(
      url,
      data => {
        try {
          const message = data as SSEMessage;
          if (message.type === 'connected') {
            // connection confirmed
          } else if (message.type === 'progress' && message.data) {
            setProgress(message.data);

            if (message.data.status === 'completed') {
              if (onCompleteRef.current) {
                onCompleteRef.current();
              }
              abort.abort();
              abortRef.current = null;
              setIsConnected(false);
            } else if (message.data.status === 'failed') {
              console.error('[SSE] Scoring failed:', message.data.error);
              if (onErrorRef.current && message.data.error) {
                onErrorRef.current(message.data.error);
              }
              abort.abort();
              abortRef.current = null;
              setIsConnected(false);
            }
          }
        } catch (error) {
          console.error('[SSE] Error handling message:', error);
        }
      },
      abort.signal
    )
      .then(() => {
        handleConnectionLost('stream ended');
      })
      .catch(err => {
        if (abort.signal.aborted) return;
        console.error('[SSE] Connection error:', err);
        handleConnectionLost(err instanceof Error ? err.message : 'unknown error');
      });
  }, [pickemId, enabled]);

  useEffect(() => {
    if (enabled && pickemId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, pickemId, connect, disconnect]);

  return {
    progress,
    isConnected,
    disconnect,
  };
}
